import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import PageTitle from "../../../widgets/PageTitle";

const DbColumnsManager = ({ initialColumns = [], onSave }) => {
  const [columns, setColumns] = useState(initialColumns);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", type: "text" });

  // Available data types
  const dataTypes = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
  ];

  // Start editing a column
  const handleEditStart = (column) => {
    setEditingId(column.id);
    setEditForm({ name: column.name, type: column.type });
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({ name: "", type: "text" });
  };

  // Save edited column
  const handleEditSave = () => {
    if (!editForm.name.trim()) {
      alert("Column name cannot be empty");
      return;
    }

    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === editingId
          ? { ...col, name: editForm.name.trim(), type: editForm.type }
          : col
      )
    );

    // Notify parent component about the change
    if (onSave) {
      const updatedColumns = columns.map((col) =>
        col.id === editingId
          ? { ...col, name: editForm.name.trim(), type: editForm.type }
          : col
      );
      onSave(updatedColumns);
    }

    setEditingId(null);
    setEditForm({ name: "", type: "text" });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add a new column
  const handleAddColumn = () => {
    const newColumn = {
      id: Date.now().toString(),
      name: `column_${columns.length + 1}`,
      type: "text",
    };
    const newColumns = [...columns, newColumn];
    setColumns(newColumns);

    if (onSave) {
      onSave(newColumns);
    }
  };

  // Delete a column
  const handleDeleteColumn = (columnId) => {
    const newColumns = columns.filter((col) => col.id !== columnId);
    setColumns(newColumns);

    if (onSave) {
      onSave(newColumns);
    }
  };

  return (
    <Container sx={{ mt: 3 }}>
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 800, margin: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <PageTitle>Define Schema</PageTitle>
          <Button variant="contained" color="primary" onClick={handleAddColumn}>
            Add Column
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                  Column Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                  Data Type
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">
                      No columns defined. Click "Add Column" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                columns.map((column) => (
                  <TableRow key={column.id} hover>
                    <TableCell>
                      {editingId === column.id ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          placeholder="Enter column name"
                        />
                      ) : (
                        <Typography variant="body1">{column.name}</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {editingId === column.id ? (
                        <Select
                          fullWidth
                          size="small"
                          value={editForm.type}
                          onChange={(e) =>
                            handleInputChange("type", e.target.value)
                          }
                        >
                          {dataTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "capitalize",
                            color: "primary.main",
                            fontWeight: "medium",
                          }}
                        >
                          {column.type}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {editingId === column.id ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Save">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={handleEditSave}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={handleEditCancel}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditStart(column)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteColumn(column.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary */}
        {columns.length > 0 && (
          <Box
            sx={{ mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}
          >
            <Typography variant="body2" color="textSecondary">
              Total columns: {columns.length} • Text:{" "}
              {columns.filter((c) => c.type === "text").length} • Number:{" "}
              {columns.filter((c) => c.type === "number").length} • Date:{" "}
              {columns.filter((c) => c.type === "date").length}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DbColumnsManager;
